import React, { useContext, useEffect, useRef, useState } from "react";
import { TextContainer, Layout, Card, FormLayout, RadioButton } from "@shopify/polaris";

// import context
import { PrivateContext, OnboardingContext } from "lib/context";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import components
import { Banner /* Sheet */ } from "lib/components";

// import constants
import constant from "lib/constant/constant";

import {
  OperatorBulkCreate,
  OperatorBulkInvite,
  OperatorFormCreate,
  OperatorGenericLink,
  OperatorInvite,
} from "./subFeatures";

const { GENERIC_LINK, CREATE_FORM, INVITE_CSV, CREATE_CSV, INVITE_FORM } = constant;
let featureItem = false;

const OperatorVendorAdd = () => {
  const { cms = {} } = useContext(PrivateContext);
  const { setIsSelectChange = () => {}, isOnboarding } = useContext(OnboardingContext);
  const [banner, setBanner] = useState({
    action: null,
    isOpen: false,
    status: "",
    title: "",
    onDismiss: null,
  });
  const [selected, setSelected] = useState(CREATE_FORM);
  const formSectionRef = useRef(null);
  const shouldScrollToFormRef = useRef(false);
  const formScrollFrameRef = useRef(null);
  const addProviderRadioOptions = [
    {
      label: cms("section.addVendorMethod.label.invite"),
      value: INVITE_FORM,
      helpText: cms("section.addVendorMethod.helpText.invite"),
    },
    {
      label: cms("section.addVendorMethod.label.bulkInvite"),
      value: INVITE_CSV,
      helpText: cms("section.addVendorMethod.helpText.bulkInvite"),
    },
    {
      label: cms("section.addVendorMethod.label.createForm"),
      value: CREATE_FORM,
      helpText: cms("section.addVendorMethod.helpText.createForm"),
    },
    {
      label: cms("section.addVendorMethod.label.bulkCreate"),
      value: CREATE_CSV,
      helpText: cms("section.addVendorMethod.helpText.bulkCreate"),
    },
    {
      label: "Generate vendor registration link",
      value: GENERIC_LINK,
      helpText: cms("section.addVendorMethod.helpText.genericLink") || cms("section.addVendorMethod.helpText.link"),
    },
  ];
  // const [sheetActive, setSheetActive] = useState(false);
  // const [sheetContent, setSheetContent] = useState("");
  // const [sheetTitle, setSheetTitle] = useState("");

  // const learnMore = (title, content) => {
  //   setSheetActive(true);
  //   setSheetTitle(title);
  //   setSheetContent(content);
  // };

  const renderComponent = (value) => {
    switch (value) {
      case CREATE_FORM:
        featureItem = constant.VENDOR_ADD;
        return <OperatorFormCreate setBanner={setBanner} /* learnMore={learnMore} */ />;
      case GENERIC_LINK:
        featureItem = constant.VENDOR_LINK;
        return <OperatorGenericLink setBanner={setBanner} /* learnMore={learnMore} */ />;
      case INVITE_FORM:
        featureItem = constant.VENDOR_INVITE;
        return <OperatorInvite setBanner={setBanner} /* learnMore={learnMore} */ />;
      case CREATE_CSV:
        featureItem = constant.VENDOR_ADD;
        return <OperatorBulkCreate setBanner={setBanner} /* learnMore={learnMore} */ />;
      case INVITE_CSV:
        featureItem = constant.VENDOR_INVITE;
        return <OperatorBulkInvite setBanner={setBanner} /* learnMore={learnMore} */ />;
      default:
        return null;
    }
  };

  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const handleSelect = (updatedValue) => {
    if (isOnboarding) {
      setIsSelectChange(true);
    }
    shouldScrollToFormRef.current = true;
    setSelected(updatedValue);
    setBanner({ isOpen: false, status: "", title: "" });
  };

  useEffect(() => {
    if (!shouldScrollToFormRef.current) {
      return undefined;
    }

    shouldScrollToFormRef.current = false;
    window.cancelAnimationFrame(formScrollFrameRef.current);

    formScrollFrameRef.current = window.requestAnimationFrame(() => {
      if (formSectionRef.current) {
        formSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });

    return undefined;
  }, [selected]);

  useEffect(() => {
    return () => {
      window.cancelAnimationFrame(formScrollFrameRef.current);
    };
  }, []);

  const onBoardingBulkActivityMessage = (
    <TextContainer>
      <Banner status={constant.CRITICAL} isOpen>
        {cms("message.error.onBoardingBulkOption")}
      </Banner>
    </TextContainer>
  );

  const getTitle = () => {
    if (isOnboarding) {
      return cms("section.onboarding.title");
    }
    return cms("section.onboarding.secondaryTitle");
  };

  const getDescription = () => {
    if (isOnboarding) {
      return cms("section.onboarding.description");
    }
    return cms("section.onboarding.description"); // need to discuss
  };

  // const captions = cms("section.onboarding.description") || [];

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      {/* title={getTitle()} description={getDescription()} */}
      <Layout.Section >
        <Card
          title={cms("section.onboarding.addSection.title")}
          // actions={isOnboarding ? [{ content: "Learn more", onAction: () => setSheetActive(!sheetActive) }] : []}
        >
          <Card.Section>
            <FormLayout>
              <TextContainer>{cms("description")}</TextContainer>
              {isOnboarding && onBoardingBulkActivityMessage}
              <div>
                <p>
                  <b>{cms("section.addVendorMethod.chooseMethod")}</b>
                </p>
                <br />
                {addProviderRadioOptions.map((option) => {
                  const isDisabled = isOnboarding && option.value !== CREATE_FORM;

                  return (
                    <div
                      key={option.value}
                      style={{
                        border: "1px solid #dfe3e8",
                        borderRadius: "12px",
                        padding: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <RadioButton
                        id={`addVendorType-${option.value}`}
                        label={option.label}
                        helpText={option.helpText}
                        checked={selected === option.value}
                        disabled={isDisabled}
                        onChange={() => handleSelect(option.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </FormLayout>
          </Card.Section>
        </Card>
      </Layout.Section>

      <div ref={formSectionRef} style={{ scrollMarginTop: "16px" }} />
      {renderComponent(selected)}
      {/* <Sheet
        isOpen={sheetActive}
        title={sheetTitle}
        onClose={() => setSheetActive(false)}
        secondaryAction={{ content: cms("button.sheet.secondaryAction"), onAction: () => setSheetActive(false) }}
        primaryAction={{ content: cms("button.sheet.primaryAction"), onAction: () => setSheetActive(false) }}
      >
        {sheetContent}
      </Sheet> */}
    </>
  );
};
export default withFeature(withErrorBoundary(OperatorVendorAdd), { feature: featureItem });
